import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { trFold, SearchClear } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Contact, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api
      .get("/customers")
      .then((r) => setCustomers(r.data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter((c) => {
    if (tab === "active" && c.is_active === false) return false;
    if (tab === "passive" && c.is_active !== false) return false;
    const s = trFold(q);
    return (
      trFold(c.company_name).includes(s) ||
      trFold(c.contact_name).includes(s) ||
      trFold(c.contact_email).includes(s)
    );
  });

  const passiveCount = customers.filter((c) => c.is_active === false).length;
  const TABS = [
    { key: "all", label: `Tümü (${customers.length})` },
    { key: "active", label: `Aktif (${customers.length - passiveCount})` },
    { key: "passive", label: `Pasif (${passiveCount})` },
  ];

  const exportExcel = async () => {
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gettproposal_musteriler.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel dosyası oluşturulamadı");
    }
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Müşteriler</h1>
          <p className="text-sm text-slate-500 mt-2">GettProposal'a kayıtlı tüm müşteriler. Satıra tıklayarak projelerini ve taleplerini görüntüleyin.</p>
        </div>
        <Button
          variant="outline"
          onClick={exportExcel}
          data-testid="customers-export-button"
          className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200"
        >
          <FileSpreadsheet size={15} /> Excel'e Aktar
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex border border-slate-200 bg-white" data-testid="customers-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`customers-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                tab === t.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="customers-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Firma, yetkili veya e-posta ara..."
            className="rounded-none bg-white border-slate-200 h-11 pl-9 pr-9"
          />
          <SearchClear value={q} onClear={() => setQ("")} testid="customers-search-clear" />
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-medium">Firma</th>
              <th className="px-5 py-3 font-medium">Yetkili</th>
              <th className="px-5 py-3 font-medium">E-posta</th>
              <th className="px-5 py-3 font-medium">Telefon</th>
              <th className="px-5 py-3 font-medium text-center">Proje</th>
              <th className="px-5 py-3 font-medium text-center">Talep</th>
              <th className="px-5 py-3 font-medium text-center">Gelen Teklif</th>
              <th className="px-5 py-3 font-medium">Kayıt Tarihi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-8 text-center text-slate-500" data-testid="customers-empty">
                  {customers.length === 0 ? "Henüz kayıtlı müşteri yok." : "Sonuç bulunamadı."}
                </td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/admin/musteriler/${c.id}`)}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                  data-testid={`customer-row-${c.id}`}
                >
                  <td className="px-5 py-4 font-semibold text-slate-900">
                    <span className="flex items-center gap-2">
                      <Contact size={14} className="text-slate-400" /> {c.company_name}
                      {c.is_active === false && (
                        <span className="text-[10px] uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5">Pasif</span>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{c.contact_name || "-"}</td>
                  <td className="px-5 py-4 text-slate-600">{c.contact_email || "-"}</td>
                  <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{c.contact_phone || "-"}</td>
                  <td className="px-5 py-4 text-center font-semibold">{c.project_count}</td>
                  <td className="px-5 py-4 text-center">{c.request_count}</td>
                  <td className="px-5 py-4 text-center">
                    {c.offer_count}
                    {c.accepted_count > 0 && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                        {c.accepted_count} kabul
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
